import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  Autocomplete,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjectDropdownsByUser } from '../../../store/ticketSlice';
import { fetchAIPlanDropdown } from '../../../store/aiDevSlice';
import { ALERT_CONDITION_TYPES, ALERT_ACTION_TYPES, ALERT_THRESHOLD_OPTIONS, CHARACTER_LIMITS } from '../../../shared/appConstants';
import { REGEX } from '../../../shared/regex';
import OverflowTooltip from '../../../shared/OverflowTooltip';

const AlertRuleModal = ({ 
  open, 
  onClose, 
  onSave, 
  alert = null,
  conditionTypes = []
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectDropdownsByUser: projectsData, projectDropdownsByUserLoading: projectsLoading } = useSelector((state) => state.tickets);
  const { conditionTypes: apiConditionTypes, userEmails } = useSelector((state) => state.aiDev);
  const { createLoading, updateLoading } = useSelector((state) => state.alerts);
  
  // Convert projects to array - ensure it's always an array
  const availableProjects = useMemo(() => {
    if (!projectsData) return [];
    if (Array.isArray(projectsData)) return projectsData;
    if (typeof projectsData === 'object') {
      const values = Object.values(projectsData);
      // If it's an object with nested structure, try to extract array
      if (values.length > 0 && Array.isArray(values[0])) {
        return values[0];
      }
      return values;
    }
    return [];
  }, [projectsData]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    projectId: '',
    conditionType: ALERT_CONDITION_TYPES.PAST_DUE_DATE,
    threshold: '1 day',
    actionType: [ALERT_ACTION_TYPES.EMAIL],
    recipients: [],
    enabled: true
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const fetchedProjectsRef = useRef(new Set());
  const isInitializedRef = useRef(false);
  const hasProjectsRef = useRef(false);

  // Fetch projects only if not already available
  useEffect(() => {
    if (open && !hasProjectsRef.current && availableProjects.length === 0) {
      dispatch(fetchProjectDropdownsByUser());
      hasProjectsRef.current = true;
    }
  }, [open, availableProjects.length, dispatch]);

  // Auto-select PM recipient when userEmails are loaded (create mode only)
  useEffect(() => {
    if (!open || alert) return;
    if (!userEmails?.length || !formData.projectId) return;

    const pmUser = userEmails.find(u => u.role && u.role.toUpperCase() === 'PM');
    if (!pmUser?.email) return;

    setFormData(prev => {
      if (prev.recipients.length > 0) return prev;
      return { ...prev, recipients: [pmUser.email] };
    });
  }, [userEmails, formData.projectId, open, alert]);

  useEffect(() => {
    if (open && !isInitializedRef.current) {
      const isEditing = !!alert;
      setIsEditMode(isEditing);
      setIsSaving(false);
      
      if (alert) {
        const recipients = alert.actions?.[0]?.recipients || [];
        const actionTypes = alert.actions
          ?.map(action => action.type)
          .filter(type => type === ALERT_ACTION_TYPES.EMAIL) || [ALERT_ACTION_TYPES.EMAIL];
        setFormData({
          name: alert.name || '',
          projectId: alert.projectId || '',
          conditionType: alert.condition?.type || ALERT_CONDITION_TYPES.PAST_DUE_DATE,
          threshold: alert.condition?.threshold || '1 day',
          actionType: actionTypes.length > 0 ? actionTypes : [ALERT_ACTION_TYPES.EMAIL],
          recipients: recipients,
          enabled: alert.enabled !== undefined ? alert.enabled : true
        });
        
        if (alert.projectId && !fetchedProjectsRef.current.has(alert.projectId)) {
          dispatch(fetchAIPlanDropdown({ projectId: alert.projectId, type: 'user_emails' }));
          dispatch(fetchAIPlanDropdown({ projectId: alert.projectId, type: 'condition_type' }));
          fetchedProjectsRef.current.add(alert.projectId);
        }
      } else {
        setFormData({
          name: '',
          projectId: '',
          conditionType: ALERT_CONDITION_TYPES.PAST_DUE_DATE,
          threshold: '1 day',
          actionType: [ALERT_ACTION_TYPES.EMAIL],
          recipients: [],
          enabled: true
        });
      }
      setErrors({});
      isInitializedRef.current = true;
    } else if (!open) {
      fetchedProjectsRef.current.clear();
      isInitializedRef.current = false;
    }
  }, [alert, open, dispatch]);

  // Auto-select first project when projects load (create mode only)
  useEffect(() => {
    if (open && !alert && availableProjects.length > 0 && !formData.projectId) {
      const defaultProjectId = availableProjects[0]?.id || availableProjects[0]?._id || availableProjects[0]?.project_id || '';
      if (defaultProjectId) {
        setFormData(prev => ({ ...prev, projectId: defaultProjectId }));
      }
    }
  }, [availableProjects, open, alert, formData.projectId]);

  // Fetch data for auto-selected project
  useEffect(() => {
    if (open && !alert && formData.projectId && !fetchedProjectsRef.current.has(formData.projectId)) {
      dispatch(fetchAIPlanDropdown({ projectId: formData.projectId, type: 'user_emails' }));
      dispatch(fetchAIPlanDropdown({ projectId: formData.projectId, type: 'condition_type' }));
      fetchedProjectsRef.current.add(formData.projectId);
    }
  }, [open, alert, formData.projectId, dispatch]);

  // Auto-select first condition type when API data loads
  useEffect(() => {
    if (open && (apiConditionTypes || conditionTypes)) {
      const conditionTypesList = apiConditionTypes || conditionTypes || [];
      if (conditionTypesList.length > 0) {
        setFormData(prev => {
          // For edit mode, match existing condition
          if (alert) {
            const matchingCondition = conditionTypesList.find(ct => 
              ct.key === alert.condition?.type || ct.value === alert.condition?.type
            );
            if (matchingCondition && prev.conditionType !== matchingCondition.key) {
              return { ...prev, conditionType: matchingCondition.key };
            }
          }
          // For create mode or if no match, use first option if current is default
          if (!alert && prev.conditionType === ALERT_CONDITION_TYPES.PAST_DUE_DATE) {
            return { ...prev, conditionType: conditionTypesList[0].key };
          }
          return prev;
        });
      }
    }
  }, [apiConditionTypes, conditionTypes, open, alert]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    
    // Handle project change specially to clear recipients
    if (field === 'projectId' && value) {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        recipients: [] // Clear recipients when project changes
      }));
      
      // Always fetch user emails when project changes
      dispatch(fetchAIPlanDropdown({ projectId: value, type: 'user_emails' }));
      fetchedProjectsRef.current.add(value);
    } else {
      setFormData(prev => {
        return {
          ...prev,
          [field]: value
        };
      });
    }
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };



  const validateEmail = (email) => {
    if (!email) return false;
    const cleanedEmail = email.replace(REGEX.emailWhitespace, '').replace(REGEX.emailInvisibleChars, '').trim();
    if (!cleanedEmail) return false;
    if (!REGEX.email.test(cleanedEmail)) return false;
    if (cleanedEmail.startsWith('.') || cleanedEmail.endsWith('.')) return false;
    if (cleanedEmail.includes('..')) return false;
    const [localPart, domainPart] = cleanedEmail.split('@');
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    if (domainPart && domainPart.includes('..')) return false;
    return true;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('projectAlerts.errors.nameRequired');
    }
    
    if (!formData.projectId) {
      newErrors.projectId = t('projectAlerts.errors.projectRequired');
    }
    
    if (!formData.threshold.trim()) {
      newErrors.threshold = t('projectAlerts.errors.thresholdRequired');
    }
    
    if (!formData.actionType || formData.actionType.length === 0) {
      newErrors.actionType = t('projectAlerts.errors.actionTypeRequired');
    }
    
    if (!formData.recipients.length) {
      newErrors.recipients = t('projectAlerts.errors.recipientsRequired');
    } else {
      // Validate all recipient emails
      const invalidEmails = formData.recipients.filter(email => !validateEmail(email));
      if (invalidEmails.length > 0) {
        newErrors.recipients = t('projectAlerts.errors.invalidEmail', { email: invalidEmails[0] });
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setIsSaving(true);
      // Find the condition type value from the current form selection
      const conditionTypeObj = (apiConditionTypes || conditionTypes || []).find(
        ct => ct.key === formData.conditionType
      );
      
      const alertData = {
        ...(alert ? { id: alert.id } : {}),
        name: formData.name,
        projectId: formData.projectId,
        projectName: availableProjects.find(p => 
          (p.id || p._id || p.project_id) === formData.projectId
        )?.name || '',
        condition: {
          type: conditionTypeObj?.value || formData.conditionType,
          threshold: formData.threshold
        },
        actions: formData.actionType.map(type => ({
          type: type,
          recipients: formData.recipients
        })),
        enabled: formData.enabled
      };
      
      onSave(alertData);
    }
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'alert-modal-paper'
      }}
    >
      <DialogTitle className="alert-modal-title">
        <Box>
          {isEditMode ? t('projectAlerts.actions.editAlertRule') : t('projectAlerts.actions.createAlertRule')}
        </Box>
      </DialogTitle>
      
      <DialogContent className="alert-modal-content">
        <Box className="alert-modal-form">
          <TextField
            label={t('projectAlerts.fields.alertName')}
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || `${formData.name?.length || 0}/${CHARACTER_LIMITS.ALERT_NAME} characters`}
            fullWidth
            variant="outlined"
            placeholder={t('projectAlerts.placeholders.alertName')}
            className="alert-modal-textfield"
            inputProps={{ maxLength: CHARACTER_LIMITS.ALERT_NAME }}
          />
          
          <FormControl fullWidth error={!!errors.projectId} disabled={projectsLoading}>
            <InputLabel>{t('projectAlerts.fields.project')}</InputLabel>
            <Select
              value={formData.projectId}
              onChange={handleChange('projectId')}
              label={t('projectAlerts.fields.project')}
              MenuProps={{
                PaperProps: {
                  className: 'alert-modal-project-menu'
                }
              }}
            >
              {projectsLoading ? (
                <MenuItem disabled value="">
                  Loading projects...
                </MenuItem>
              ) : availableProjects.length === 0 ? (
                <MenuItem disabled value="">
                  No projects available
                </MenuItem>
              ) : (
                availableProjects.map((project) => {
                  const projId = project.id || project._id || project.project_id;
                  const projectName = project.name || project.title || `Project ${projId}`;
                  return (
                    <MenuItem key={projId} value={projId}>
                      {projectName}
                    </MenuItem>
                  );
                })
              )}
            </Select>
            {errors.projectId && (
              <Typography variant="caption" color="error" className="alert-modal-error-text">
                {errors.projectId}
              </Typography>
            )}
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>{t('projectAlerts.fields.conditionType')}</InputLabel>
            <Select
              value={formData.conditionType}
              onChange={handleChange('conditionType')}
              label={t('projectAlerts.fields.conditionType')}
            >
              {(apiConditionTypes || conditionTypes || []).map((condition) => (
                <MenuItem key={condition.key} value={condition.key}>
                  {condition.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth error={!!errors.threshold}>
            <InputLabel>{t('projectAlerts.fields.threshold')}</InputLabel>
            <Select
              value={formData.threshold}
              onChange={handleChange('threshold')}
              label={t('projectAlerts.fields.threshold')}
            >
              {ALERT_THRESHOLD_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {errors.threshold && (
              <Typography variant="caption" color="error" className="alert-modal-error-text">
                {errors.threshold}
              </Typography>
            )}
          </FormControl>
          
          <FormControl fullWidth error={!!errors.actionType}>
            <InputLabel>{t('projectAlerts.fields.actionType')}</InputLabel>
            <Select
              multiple
              value={formData.actionType}
              onChange={handleChange('actionType')}
              label={t('projectAlerts.fields.actionType')}
              renderValue={(selected) => (
                <Box className="alert-modal-action-type-chips">
                  {selected.map((value) => (
                    <Chip key={value} label={t(`projectAlerts.actionTypes.${value.toLowerCase()}`)} size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value={ALERT_ACTION_TYPES.EMAIL}>{t('projectAlerts.actionTypes.email')}</MenuItem>
              {/* Slack option disabled for now */}
              {/* <MenuItem value={ALERT_ACTION_TYPES.SLACK}>{t('projectAlerts.actionTypes.slack')}</MenuItem> */}
            </Select>
            {errors.actionType && (
              <Typography variant="caption" color="error" className="alert-modal-error-text">
                {errors.actionType}
              </Typography>
            )}
          </FormControl>
          
          <Box className="alert-modal-recipients-field">
            <Autocomplete
              multiple
              freeSolo
              options={userEmails?.filter(user => user?.email)?.map(user => user.email) || []}
              value={formData.recipients}
              disabled={!formData.projectId}
              componentsProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                },
              }}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <OverflowTooltip 
                      title={option} 
                      tooltipWidth={400}
                      placement="top-start"
                      className="overflow-tooltip-flex"
                    />
                  </li>
                );
              }}
              onChange={(event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  recipients: newValue
                }));
                if (errors.recipients) {
                  setErrors(prev => ({
                    ...prev,
                    recipients: ''
                  }));
                }
              }}
              renderTags={(value, getTagProps) => {
                const displayEmails = value.slice(0, 10);
                const remainingCount = value.length - 10;
                
                return (
                  <Box className="alert-modal-recipients-chips">
                    {displayEmails.map((option, index) => (
                      <OverflowTooltip
                        key={option}
                        title={option}
                        tooltipWidth={300}
                        placement="top-start"
                        component={Chip}
                        {...getTagProps({ index })}
                        label={option}
                        size="small"
                        className="alert-modal-recipient-chip"
                        sx={{
                          maxWidth: '350px',
                          flexShrink: 0,
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            maxWidth: '100%'
                          }
                        }}
                      />
                    ))}
                    {remainingCount > 0 && (
                      <Tooltip
                        title={value.slice(10).join(', ')}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            className: 'app-tooltip',
                            sx: { maxWidth: '400px' }
                          }
                        }}
                      >
                        <Chip
                          label={`+${remainingCount}`}
                          size="small"
                          className="alert-modal-recipient-chip"
                          sx={{
                            cursor: 'help',
                            flexShrink: 0
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('projectAlerts.fields.recipients')}
                  error={!!errors.recipients}
                  helperText={errors.recipients || (!formData.projectId ? t('projectAlerts.messages.selectProjectFirst') : t('projectAlerts.messages.typeEmailPressEnter'))}
                  placeholder={formData.projectId ? t('projectAlerts.placeholders.enterEmail') : t('projectAlerts.messages.selectProjectFirst')}
                  variant="outlined"
                />
              )}
              ListboxProps={{
                className: 'alert-modal-recipients-listbox'
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions className="alert-modal-actions">
        <Button 
          onClick={onClose}
          variant="outlined"
          size="large"
        >
          {t('projectAlerts.actions.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          className="alert-modal-save-button"
          disabled={isSaving || createLoading || updateLoading}
        >
          {isEditMode ? t('projectAlerts.actions.update') : t('projectAlerts.actions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertRuleModal;
